import * as userActions from 'actions/userActions';
import JSDButton from 'components/popups/JSDButton';
import { useAppDispatch } from 'hooks';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, FormControl, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import App from '../../App';
import { getUser } from '../../reducers/selectors';
import GoogleLogin from './GoogleLogin';
import PasswordTextBox from './PasswordTextBox';
import * as http from '../../http';
import { useHistory, useLocation } from 'react-router-dom';

interface ILoginError {
  present: boolean;
  message?: string;
}

function Login({ oidc }: { oidc: boolean }) {
  const user = useSelector(getUser);
  const [showGoogleLogin, setShowGoogleLogin] = useState<boolean>(false);
  const [showLoginBox, setShowLoginBox] = useState<boolean>(!oidc);
  const [error, setError] = useState<ILoginError>({ present: false });

  return (
    <React.Fragment>
      {!user.authenticated ? (
        <React.Fragment>
          <div className="background-login" />
          <div className="login-box">
            <div className="login-card">
              <div
                className="feed-img"
                onDoubleClick={() => {
                  if (oidc) {
                    setShowLoginBox(true);
                  }
                  setShowGoogleLogin(true);
                }}
              />
              {oidc && <OidcLoginForm setError={setError} />}
              {showLoginBox && <LoginForm showGoogleLogin={showGoogleLogin} setError={setError} />}
              {error.present && (
                <div style={{ textAlign: 'center' }}>
                  <strong style={{ color: 'var(--danger)' }}>{error.message}</strong>
                </div>
              )}
            </div>
            <JSDButton isVisible={false} />
          </div>
        </React.Fragment>
      ) : (
        <App />
      )}
    </React.Fragment>
  );
}

const OidcLoginForm = ({ setError }: { setError: (_: ILoginError) => void }) => {
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const oidcLoginFailed = queryParams.get('oidc_login_failed');

    if (oidcLoginFailed !== null) {
      // this could happen after the login callback is called, and no real interaction was previously done
      setError({ present: true, message: 'Log in with Identity Provider failed' });
      queryParams.delete('oidc_login_failed');
      history.replace({ search: queryParams.toString() });
    }
  }, []);

  const oidcLogin = () => {
    http
      .post<string>(`/rest/oidc/login`)
      .then(redirectUri => {
        window.location.assign(redirectUri);
      })
      .catch(() => setError({ present: true, message: 'Log in with Identity Provider failed' }));
  };

  return (
    <div className="oidc-login-button">
      <Button variant="outline-primary" className="login-button" onClick={() => oidcLogin()}>
        Sign in with Identity Provider
      </Button>
    </div>
  );
};

const LoginForm = ({ showGoogleLogin, setError }: { showGoogleLogin: boolean; setError: (_: ILoginError) => void }) => {
  const dispatch = useAppDispatch();

  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  function login() {
    userActions
      .login(username, password)
      .then(res => {
        dispatch({
          type: 'SET_USER',
          payload: {
            ...res,
            authenticated: true,
          },
        });
        setUsername('');
        setPassword('');
      })
      .catch(() => {
        setError({ present: true, message: 'Login failed' });
      });
  }

  return (
    <Form
      style={{ marginTop: '2em' }}
      onKeyPress={e => {
        if (e.charCode === 13) {
          login();
        }
      }}
    >
      <FormGroup as={Row} className="login-row">
        <FormLabel column sm={2} md={{ offset: 1 }} className="login-label">
          Username
        </FormLabel>
        <Col sm={8}>
          <FormControl
            size="sm"
            type="text"
            placeholder="Username"
            onChange={e => {
              setUsername(e.target.value);
            }}
          />
        </Col>
      </FormGroup>
      <FormGroup as={Row} className="login-row">
        <Form.Label column sm={4} md={{ offset: 1 }} className="login-label">
          Password
        </Form.Label>
        <Col sm={8}>
          <PasswordTextBox
            placeholder="Password..."
            password={password}
            setPassword={e => {
              setPassword(e.target.value);
            }}
          />
        </Col>
      </FormGroup>
      <Button variant="outline-primary" className="login-button" onClick={() => login()}>
        Log in
      </Button>
      {showGoogleLogin && <GoogleLogin setError={setError} />}
    </Form>
  );
};

export default Login;
